import { type ReactNode, createContext, useContext } from "react";
import { LanguageSwitcher } from "#/components/i18n/LanguageSwitcher.js";
import { LocaleReconciler } from "#/components/i18n/LocaleReconciler.js";
import { MachineTranslationBanner } from "#/components/i18n/MachineTranslationBanner.js";
import type { TranslateFn } from "#/lib/i18n.js";
import {
  DEFAULT_LOCALE,
  LOCALE_URL_SEGMENTS,
  type LocaleCode,
  isLocaleCode,
  resolveSegment,
} from "#/lib/i18n/locales.js";

const EXCLUDED_ROUTE_ROOTS = new Set(["admin", "superadmin", "login", "embed", "forbidden"]);

type PublicChromeProps = {
  locale: string;
  t: TranslateFn;
  children: ReactNode;
};

const PublicChromeContext = createContext(false);

type BrandLogoProps = {
  href: string;
  t: TranslateFn;
  size?: "sm" | "md";
};

export function stripLocalePrefix(pathname: string): string {
  const [pathnameOnly = "/"] = pathname.split("?");
  const segments = pathnameOnly.split("/").filter(Boolean);
  const first = segments[0];

  if (first && resolveSegment(first)) {
    const withoutLocale = `/${segments.slice(1).join("/")}`;
    return withoutLocale === "/" ? "/" : withoutLocale;
  }

  return pathnameOnly || "/";
}

export function shouldShowPublicChrome(pathname: string | undefined): boolean {
  const logicalPath = stripLocalePrefix(pathname ?? "/");
  const firstSegment = logicalPath.split("/").filter(Boolean)[0];

  if (!firstSegment) return true;
  return !EXCLUDED_ROUTE_ROOTS.has(firstSegment);
}

export function getLocalizedHref(locale: string, href: string): string {
  const safeHref = href.startsWith("/") ? href : `/${href}`;
  if (locale === DEFAULT_LOCALE) return safeHref;
  // The URL segment is the lowercased form of the locale code (e.g. `zh-cn` for `zh-CN`).
  const segment = locale.toLowerCase();
  if (!LOCALE_URL_SEGMENTS.has(segment)) return safeHref;
  if (safeHref === "/") return `/${segment}`;
  return `/${segment}${safeHref}`;
}

function BrandLogo({ href, t, size = "md" }: BrandLogoProps) {
  const imageSize = size === "sm" ? 32 : 40;

  return (
    <a
      href={href}
      aria-label={t("chrome.brandAria")}
      className="inline-flex min-h-11 min-w-0 items-center gap-3 text-[color:var(--color-fg-default)] no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg-base)]"
    >
      <img
        src="/branding/hantavir-us-mark.png"
        alt=""
        width={imageSize}
        height={imageSize}
        className="shrink-0"
        style={{ inlineSize: imageSize, blockSize: imageSize }}
      />
      <span
        className={[
          "truncate font-semibold leading-none tracking-normal",
          size === "sm" ? "text-base" : "text-lg",
        ].join(" ")}
      >
        hantavir.us
      </span>
    </a>
  );
}

export function PublicHeader({ locale, t }: Omit<PublicChromeProps, "children">) {
  const homeHref = getLocalizedHref(locale, "/");
  const safeLocale: LocaleCode = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;
  const navItems = [
    {
      href: getLocalizedHref(locale, "/subscribe"),
      label: t("chrome.nav.subscribe"),
    },
    {
      href: getLocalizedHref(locale, "/download"),
      label: t("chrome.nav.download"),
    },
  ];

  return (
    <header
      data-public-chrome="header"
      className="border-b border-[color:var(--color-border-subtle)] bg-[color:var(--color-bg-base)]"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-stretch gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 lg:px-8">
        <BrandLogo href={homeHref} t={t} />
        <nav
          aria-label={t("chrome.nav.label")}
          className="flex min-w-0 flex-wrap items-center gap-1 sm:justify-end sm:gap-2"
        >
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 text-sm font-medium text-[color:var(--color-fg-muted)] no-underline transition-colors duration-150 hover:bg-[color:var(--color-bg-surface)] hover:text-[color:var(--color-fg-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)] sm:px-3"
            >
              {item.label}
            </a>
          ))}
          <LanguageSwitcher currentLocale={safeLocale} variant="navbar" t={t} />
        </nav>
      </div>
    </header>
  );
}

export function PublicFooter({ locale, t }: Omit<PublicChromeProps, "children">) {
  const homeHref = getLocalizedHref(locale, "/");
  const year = new Date().getFullYear();
  const safeLocale: LocaleCode = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;
  const links = [
    {
      href: "/privacy",
      label: t("chrome.footer.privacy"),
    },
    {
      href: "/terms",
      label: t("chrome.footer.terms"),
    },
    {
      href: getLocalizedHref(locale, "/accessibility"),
      label: t("chrome.footer.accessibility"),
    },
    {
      href: getLocalizedHref(locale, "/docs/user/index"),
      label: t("chrome.footer.docs"),
    },
  ];

  return (
    <footer
      data-public-chrome="footer"
      className="mt-12 border-t border-[color:var(--color-border-subtle)] bg-[color:var(--color-bg-surface)]"
    >
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 md:grid-cols-[minmax(0,1fr)_auto] lg:px-8">
        <div className="max-w-xl">
          <BrandLogo href={homeHref} t={t} size="sm" />
          <p className="mt-3 max-w-prose text-sm leading-6 text-[color:var(--color-fg-muted)]">
            {t("chrome.footer.description")}
          </p>
          <p className="mt-4 text-xs text-[color:var(--color-fg-subtle)]">
            {t("chrome.footer.rights", { year })}
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 md:items-end">
          <nav
            aria-label={t("chrome.footer.navLabel")}
            className="flex flex-wrap items-start gap-x-4 gap-y-2 text-sm md:justify-end"
          >
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex min-h-11 min-w-11 items-center justify-center text-[color:var(--color-fg-muted)] no-underline transition-colors duration-150 hover:text-[color:var(--color-fg-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)]"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <LanguageSwitcher currentLocale={safeLocale} variant="footer" t={t} />
        </div>
      </div>
    </footer>
  );
}

export function PublicChrome({ locale, t, children }: PublicChromeProps) {
  const isNestedChrome = useContext(PublicChromeContext);
  if (isNestedChrome) {
    return <>{children}</>;
  }

  const safeLocale: LocaleCode = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;

  return (
    <PublicChromeContext.Provider value={true}>
      <LocaleReconciler serverLocale={safeLocale} currentLogicalPath="/" />
      <MachineTranslationBanner locale={safeLocale} namespace="landing" t={t} />
      <PublicHeader locale={locale} t={t} />
      {children}
      <PublicFooter locale={locale} t={t} />
    </PublicChromeContext.Provider>
  );
}
